import { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Grid,
  TextField
} from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { PrimaryButton, SearchAndSelect, SecondaryButton } from '@components';
import useSearchOfficials, { SearchResultOption } from '@hooks/useSearchOfficials';
import { useUser } from '@hooks/useUser';
import { updateOperationActivity, OperationActivity } from '../../../../networkRequest/operationsService';
import Utils from '../../../../utils/utils';
import { parseActivityIncidentFormData, defaultActivityIncidentFormErrors, validateActivityIncidentField, ActivityIncidentFormData, ActivityIncidentFormErrors } from '../../utils/formValidation';

dayjs.extend(customParseFormat);

interface EditActivityDialogProps {
  operationId: string;
  open: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  onEditActivity: () => Promise<void>;
  selectedActivity: OperationActivity | null;
  setSelectedActivity: Dispatch<SetStateAction<OperationActivity | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

const EditActivityDialog = ({
  operationId,
  open,
  onClose,
  onEditActivity,
  selectedActivity,
  setSelectedActivity,
  setError,
}: EditActivityDialogProps) => {
  const { user } = useUser();
  const { searchResultOptions, handleSearch, parseOfficials } = useSearchOfficials({ operationId });

  const [activityOfficialsAndTeams, setActivityOfficialsAndTeams] = useState<{ id: string, label: string }[]>([]);
  const [formErrors, setFormErrors] = useState<ActivityIncidentFormErrors>(defaultActivityIncidentFormErrors);
  const [formData, setFormData] = useState<ActivityIncidentFormData>({
    date: dayjs(),
    time: dayjs(),
    description: '',
  });
  const [saving, setSaving] = useState<boolean>(false);
  const formRef = useRef(null);

  const hasErrors = Object.values(formErrors).some((error) => error);

  useEffect(() => {
    if (!selectedActivity) return;
    setFormData({
      date: dayjs(selectedActivity?.performed_at),
      time: dayjs(selectedActivity?.performed_at),
      description: selectedActivity?.description ?? '',
    });
  }, [selectedActivity]);

  useEffect(() => {
    if (!selectedActivity?.officials.length) return;

    setActivityOfficialsAndTeams(selectedActivity.officials.map((official) => ({
      id: official.id,
      label: Utils.fullName(official.agency_official.first_name, official.agency_official.last_name),
      searchResult: {
        kind: 'operation_agency_official',
      },
    })));
  }, [selectedActivity])

  const handleCancel = () => {
    setSelectedActivity(null);
    setFormErrors(defaultActivityIncidentFormErrors);
    onClose(false);
  };

  const handleSelectSearchResult = (_e, newValue: SearchResultOption[], reason) => {
    if (reason === 'clear' || !newValue.length) {
      setActivityOfficialsAndTeams([]);
    }

    if (!newValue) {
      return;
    }

    const parsedResults = parseOfficials(newValue);
    setActivityOfficialsAndTeams(parsedResults);
  }

  const handleChangeInput = (field: keyof typeof formData, value: any) => {
    const newFormData = { ...formData, [field]: value };
    validateActivityIncidentField({ field, value, stateHandler: setFormErrors, formErrors });
    setFormData(newFormData);
  };

  // submit button is disabled if there are any form errors
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedActivity || hasErrors) return;

    const { date, time, description } = parseActivityIncidentFormData(formData);

    const payload = {
      performed_at: Utils.createTimestamp(date, time),
      description,
      official_ids: activityOfficialsAndTeams.map((official) => official.id),
    };

    setSaving(true);
    try {
      const { data, error } = await updateOperationActivity({
        accessToken: user?.accessToken,
        activityId: selectedActivity?.id,
        payload,
      });

      if (!data && error) {
        setError(error);
        return;
      }

      if (data) {
        onEditActivity();
        setSelectedActivity(null);
        setFormErrors(defaultActivityIncidentFormErrors);
        onClose(false);
      }
    } catch {
      setError('Failed to update activity');
    } finally {
      setSaving(false);
    }
  }

  if (!selectedActivity) {
    return null;
  }

  return (
    <Dialog
      open={open}
      maxWidth='md'
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8, width: '783px' } }
      }}
    >
      <DialogTitle sx={{ padding: 3 }} variant='h5'>
        Edit Activity
      </DialogTitle>

      <form ref={formRef} onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ padding: '40px 24px 36px 24px' }}>
          <Grid container sx={{
            mt: '8px'
          }}>
            <Grid size={12} container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    name='date'
                    label='Date'
                    value={formData.date}
                    disableFuture
                    onChange={(value) => handleChangeInput('date', value)}
                    slotProps={{
                      textField: {
                        required: true,
                        error: !!formErrors.date,
                        onChange: (value) => handleChangeInput('date', value),
                        helperText: formErrors.date ?? '',
                        slotProps: { inputLabel: { shrink: true } },
                        sx: {
                          width: '100%',
                          '& fieldset': {
                            borderRadius: '10px',
                            borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    name='time'
                    label='Time'
                    value={formData.time}
                    onChange={(value) => handleChangeInput('time', value)}
                    slotProps={{
                      textField: {
                        required: true,
                        error: !!formErrors.time,
                        onChange: (value) => handleChangeInput('time', value),
                        helperText: formErrors.time ?? '',
                        sx: {
                          width: '100%',
                          '& fieldset': {
                            borderRadius: '10px',
                            borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Grid size={12} sx={{
              mt: '16px'
            }}>
              <TextField
                disabled={saving}
                multiline
                fullWidth
                required
                name='description'
                label='Description'
                error={!!formErrors.description}
                onChange={(e) => handleChangeInput('description', e.target.value)}
                helperText={formErrors.description ?? ''}
                value={formData.description}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </Grid>
            <Grid size={12} sx={{
              mt: '16px'
            }}>
              <SearchAndSelect
                label='Add Personnel'
                placeholder='Search for individuals or teams'
                options={searchResultOptions}
                onInputChange={handleSearch}
                value={activityOfficialsAndTeams}
                onChange={handleSelectSearchResult}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            paddingTop: 1,
            paddingBottom: 3,
            paddingRight: 3,
            paddingLeft: 3,
          }}
        >
          <SecondaryButton action={handleCancel}>
            Cancel
          </SecondaryButton>

          <PrimaryButton
            type='submit'
            disabled={saving || hasErrors}
          >
            Submit
          </PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditActivityDialog;